import Vue, { CreateElement } from '@src/vue'
import { Component, Prop } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import ProgressSteps from '@src/elements/progress-steps/progress-steps'
import { NotificationTypes } from '@src/util/constants'
import ApiClient from '@src/lib/api_client'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { Icon } from '@src/elements/icon/icon'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'
import Member from '@src/data/member'
import { API } from '@src/lib/api_path'
import Checkup, { CheckupState, CheckupStep } from '@src/data/checkup'
import EventBus from '@src/lib/event_bus'

import './wizard.sass'
import { Button } from '@src/elements/button/button'

type MemberResponse = { member: Member }
type CheckupResponse = { checkup: Checkup }

@Component({
  computed: {
    checkup () {
      return this.$store.state.checkup
    },
    steps () {
      return this.$store.state.checkup ? this.$store.state.checkup.getSteps() || [] : []
    },
    step () {
      return this.$store.state.step
    }
  }
})
export default class Wizard extends Vue {

  @Prop({ required: true })
  protected basePath: string

  @Prop({ required: true })
  protected closeCallback: () => void

  @Prop({ required: false, default: 1 })
  protected startStep: number

  protected isReadonly: boolean

  protected checkup: Checkup
  protected steps: CheckupStep[]
  protected member: Member

  protected apiClient: ApiClient = new ApiClient()
  protected loading: boolean = true
  protected loadingMember: boolean = true

  protected step: number
  private stepName: string = ''

  private showConfirmPopup: boolean = false
  private confirmMessage: any = ''
  private notificationType: string = ''
  private noSaveTimer: null | NodeJS.Timeout | number = null
  private canSave = false

  constructor () {
    super()
  }

  mounted () {
    this.noSaveTimer = setTimeout(() => {
      this.canSave = true
    }, 10000)
  }

  updated () {
    if (!this.$route.path.includes(this.basePath)) return
    let urlStepName = this.$route.path.split('/').slice(-1)[0]
    // if back on browser change step in accordance with the stepName in the url
    if (this.stepName && urlStepName !== this.stepName) {
      let index = this.steps.map((s) => s.type.toString()).indexOf(urlStepName)
      if (index === -1) { // close the wizard
        this.goToStep(this.startStep)
        this.showCloseConfirm()
      } else {
        this.$store.dispatch('changeStep', index + 1)
      }
    }
  }

  nextStep () {
    this.$store.dispatch('changeStep', this.step + 1)
    if (this.step > this.steps.length) {
      this.goToCompleted()
    } else {
      this.goToStep(this.step)
    }
  }

  goToStep (index: number) {
    this.$store.dispatch('changeStep', index)

    // config for the components to be loaded
    this.stepName = this.steps[index - 1].type.replace('_','-')

    // load route - component
    if (this.basePath) {
      this.$router.push(`${this.basePath}/${this.checkup.id}/${this.stepName}`)
    }
  }

  goToCompleted () {
    if (!this.isReadonly) {
      this.$router.push(`${this.$route.path}/completed`)
    }
  }

  showCloseConfirm () {
    if (this.isReadonly || this.checkup.isComplete()) {
      this.closeCallback()
    } else {
      this.confirmMessage = this.$t('PAGE.MEMBERS.CHECKUPS.CLOSE_CONFIRMATION')
      this.notificationType = NotificationTypes.CONFIRM
      this.showConfirmPopup = true
    }
  }

  saveAndReturn () {
    if (this.canSave) {
      this.closeBuilder(true)
    }
  }

  restartWizard () {
    this.goToStep(1)
  }

  closeBuilder (confirm) {
    if (confirm === true && this.closeCallback) {
      this.closeCallback()
    }
    this.showConfirmPopup = false
  }

  back () {
    this.goToStep(this.step - 1)
  }

  next () {
    this.nextStep()
  }

  render (h: CreateElement) {
    if (this.loading || this.loadingMember) {
      return <Loader />
    }

    return <div class='wizard-wrapper'>
      <Icon ref='closeBtn' class='close-btn' name='util/close-circled' click={this.showCloseConfirm} />
      <ConfirmModal show={this.showConfirmPopup}
                    closeCallback={this.closeBuilder}
                    message={this.confirmMessage}
                    type={this.notificationType} />

      <ProgressSteps steps={this.steps} step={this.step} changeStepCallback={this.goToStep} />

      <div class={`scroll-content wizard-content ${this.isReadonly ? 'readonly' : ''}`} id='wizard-content'>
        <router-view useWizard={true} member={this.member} isReadonly={this.isReadonly} />
      </div>

      {this.step !== this.steps.length ?
        <div class='row wizard-actions steps-actions'>
          {this.step > 1 ? <Button label={this.$t('BACK')} click={this.back} ref='backBtn' type='outline' /> : null}
          <Button label={this.$t('NEXT')} click={this.next} ref='nextBtn' type='elevated' />
        </div>
        : <div class='row wizard-actions'>
          {!this.isReadonly ?
            <Button ref='restartCheckup' class='restart'
                    label={this.basePath.includes('onboarding') ? this.$t('PAGE.MEMBERS.ONBOARDING.RESTART_ONBOARDING') : this.$t('PAGE.MEMBERS.CHECKUPS.RESTART_CHECKUP')}
                    click={this.restartWizard} type='outline' /> : null}
            <Button ref='saveReturn' class={`save-return ${!this.canSave && 'disabled'}`} label={this.$t(`PAGE.MEMBERS.CHECKUPS.${this.isReadonly ? 'RETURN_TO_CHECKUPS' : 'SAVE_RETURN'}`)} click={this.saveAndReturn}
                  type='elevated' />
        </div>}
    </div>
  }

  created () {
    EventBus.on('reloadSelectedMember', this.loadMember)

    return Promise.all([this.loadMember(), this.loadCheckup()])
  }

  beforeDestroy () {
    if (typeof this.noSaveTimer === 'number') {
      clearTimeout(this.noSaveTimer)
    }
    if (typeof this.noSaveTimer === 'object') {
      clearTimeout(this.noSaveTimer as NodeJS.Timeout)
    }
    EventBus.off('reloadSelectedMember', this.loadMember)
  }

  protected loadMember () {
    return this.apiClient.get<MemberResponse>(API.TRAINERS_API.MEMBER_DETAILS.replace('{memberId}', this.$route.params.id))
      .then((response: MemberResponse) => this.$store.dispatch('setSelectedMember', new Member(camelcaseKeys(response.member))))
      .catch(error => console.error('failed to retrieve member', error))
      .finally(() => this.loadingMember = false)
  }

  protected loadCheckup () {
    if (this.$route.params.checkupId) {
      return this.apiClient.get<CheckupResponse>(`${API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id)}/${this.$route.params.checkupId}`)
        .then((response) => {
          let checkup = new Checkup(camelcaseKeys(response.checkup))
          if (this.basePath.includes('onboarding')) {
            checkup.startOnboarding()
          }
          this.$store.dispatch('setCheckup', checkup)

          let urlStepName = this.$route.path.split('/').slice(-1)[0].replace('-', '_')
          let index = this.steps.map((s) => s.type.toString()).indexOf(urlStepName)

          if (index === -1) {
            this.goToStep(this.step || this.startStep)
          } else {
            this.goToStep(index + 1)
          }
        })
        .catch(err => console.error('Failed to retrieve checkup, ', err))
        .finally(() => {
          this.loading = false
          this.isReadonly = this.checkup.state === CheckupState.COMPLETED
        })

    } else {
      this.goToStep(this.step || this.startStep)
      this.loading = false
    }
  }
}
